import { solid } from '@fortawesome/fontawesome-svg-core/import.macro';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Button, Dialog, Grid, Tooltip } from '@material-ui/core';
import React from 'react';
import { useSelector } from 'react-redux';
import { translate } from '../../../common/intl';
import { documentTypeSelectors } from '../../../state/ducks/documentRevisions/documentType';
import { HeaderTableType } from '../../../state/ducks/documentRevisions/types';
import { ApplicationState } from '../../../state/reducers';
import GridTitle from '../../components/KendoGrid/GridTitle/presenter';
import Text from '../../components/Text';
import ExportListIcon from '../../components/common/kendo/ExportListIcon';
import ExportListDialog from '../../document.revision/list/ExportListDialog/ExportListDialog';
import { DialogHandler } from '../../hooks/useDialog';
import { withThemeNext } from '../../layout/theme-next';
import { TemplateOption } from '../form/types';
import { styles } from './FormList.styles';
import FormListContent from './dialog/FormListContent';
import FormListHeader from './dialog/FormListHeader';

const CHANGE_REQUEST_CATEGORY = 'Approval Request';
const NextThemeTooltip = withThemeNext(Tooltip);

interface Props {
  templateOptions: TemplateOption[]
  dialog: DialogHandler
  handleCreateButton: () => void
  handleTemplateSelection: (option: TemplateOption) => void
  headers: HeaderTableType[]
  data: any[]
  exportDialog: DialogHandler
  isGrouped: boolean
  isLoading?: boolean
}

const FormListPresenter: React.FunctionComponent<Props> = ({
  templateOptions,
  dialog,
  exportDialog,
  handleCreateButton,
  handleTemplateSelection,
  headers,
  data,
  isGrouped,
  isLoading,
}) => {
  const classes = styles();
  const canCreateAR = useSelector(
    (state: ApplicationState) => documentTypeSelectors.canCreateCategory(state, CHANGE_REQUEST_CATEGORY),
  );
  return (
    <>
      <Grid container alignItems="center" justify="space-between">
        <Grid item>
          <GridTitle translation="page.table.title.changeRequest" />
        </Grid>
        <Grid item>
          <Button
            variant="outlined"
            className={classes.exportListbutton}
            id="ListPresenter-export-list"
            data-cy="document.revision.export.list"
            onClick={exportDialog.open}
            disabled={isGrouped}
          >
            <ExportListIcon isGrouped={isGrouped} />
            <Text translation="document.revision.export.list" />
          </Button>
          <ExportListDialog
            {...{ headers, data }}
            dialog={exportDialog}
            customFileName="DocumentList"
            isMultiplePagesDownloadAllowed
            isARRecord
          />
          <NextThemeTooltip
            title={!canCreateAR ? translate('documentRevision.create.not.allowed.msg') : ''}
            disableHoverListener={canCreateAR}
            arrow
            placement="top"
          >
            <span className={classes.createButtonTooltipTarget}>
              <Button
                id="ChangeRequest-create"
                data-cy="change.request.create"
                onClick={handleCreateButton}
                className={classes.createButton}
                startIcon={
                  !canCreateAR ? <FontAwesomeIcon className={classes.createButtonLockIcon} icon={solid('lock')} /> : undefined
                }
                disabled={!canCreateAR}
              >
                <Text translation="change.request.create" />
              </Button>
            </span>
          </NextThemeTooltip>
        </Grid>
      </Grid>
      <Dialog
        open={dialog.isOpen}
        onClose={dialog.close}
        classes={{ paper: classes.dialogPaper }}
      >
        <FormListHeader {...{ dialog }} />
        <FormListContent {...{ dialog, templateOptions, handleTemplateSelection, isLoading }} />
      </Dialog>
    </>
  );
};

export default FormListPresenter;
